import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Card, Grid, Stack, Typography, InputAdornment } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { ProductType } from 'src/type/product';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import UpdatedText from 'src/components/ui-kit/updated-text';
import GoldPriceChart from './gold-price-chart';

type Props = {
  product: ProductType;
  forceReload: () => void;
};

export default function GoldPriceInputCard({ product, forceReload }: Props) {
  const { name, productPriceLog } = product;

  const latestSellingPrice = productPriceLog?.[0]?.priceSellPerUnit;
  const latestBuyingPrice = productPriceLog?.[0]?.priceBuyPerUnit;

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      sellingPrice: latestSellingPrice || 0,
      buyingPrice: latestBuyingPrice || 0,
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = methods;

  const onSubmit = handleSubmit(async (value) => {
    try {
      if (!isDirty) {
        return;
      }
      const body = {
        productId: product.id,
        priceBuyPerUnit: value.buyingPrice,
        priceSellPerUnit: value.sellingPrice,
        unitName: 'บาท',
        currency: 'THB',
      };
      await axios.post(endpoints.product.priceLog, body);
      forceReload();
      // reset();
      enqueueSnackbar('อัพเดทราคาสำเร็จ', { variant: 'success' });
    } catch (e) {
      toastError(e);
    }
  });

  const displayUpdateBy = () => {
    if (!productPriceLog?.[0]?.approvedBy) return null;

    const { approvedBy, approvedByUser, updatedAt } = productPriceLog[0];

    const updatedByText = approvedByUser ? `(${approvedByUser.firstName})` : '';

    const updatedBy = approvedBy === 'admin' ? `${approvedBy} ${updatedByText}` : approvedBy;

    return (
      <Typography variant="caption" color="text.secondary" textAlign="end">
        <UpdatedText user={updatedBy} updatedAt={updatedAt} />
      </Typography>
    );
  };

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Card
        sx={{
          height: '100%',
          boxShadow: 0,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          px: 3,
          py: 4,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" gutterBottom>
            {name}
          </Typography>
          <Grid container spacing={3} mb={2}>
            <Grid item xs={12} md={7} p={0}>
              <GoldPriceChart priceLogs={productPriceLog} />
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack spacing={3}>
                {displayUpdateBy()}
                <RHFTextField
                  name="buyingPrice"
                  label="รับซื้อ"
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography fontWeight="bold" color="success.main">
                          THB
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <RHFTextField
                  name="sellingPrice"
                  label="ขายออก"
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography fontWeight="bold" color="success.main">
                          THB
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <LoadingButton
                    color="inherit"
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                  >
                    บันทึก
                  </LoadingButton>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Card>
    </FormProvider>
  );
}

const schema = Yup.object().shape({
  sellingPrice: Yup.number()
    .typeError('ราคาขายออกต้องเป็นตัวเลข')
    .moreThan(0, 'จำเป็นต้องระบุ')
    .required('จำเป็นต้องระบุ'),
  buyingPrice: Yup.number()
    .typeError('ราคารับซื้อต้องเป็นตัวเลข')
    .moreThan(0, 'จำเป็นต้องระบุ')
    .required('จำเป็นต้องระบุ'),
});
